import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';
import { DatePicker } from 'react-datepicker';
import { twMerge } from 'tailwind-merge';

import 'react-datepicker/dist/react-datepicker.css';
import './date-time-input.css';

import { dateTime } from '@/_shared';

import { Input } from './input';
import type { InputProps } from './type';

const DATE_TIME_FORMAT = 'MM/dd/yyyy h:mm aa';
const TIME_INTERVAL_MINUTES = 15;

/** Class used to scope all datetime-specific styles (see date-time-input.css). */
const CALENDAR_CLASS = 'stag-date-time-picker';

export type DateTimeInputProps = Omit<InputProps, 'value'> & {
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  minDate?: Date;
  maxDate?: Date;
  popperPlacement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  renderInPortal?: boolean;
  preventOpenOnFocus?: boolean;
};

function parseDateTimeValue(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const parsed = dateTime.parse(value);
  return parsed.isValid() ? parsed.toDate() : null;
}

export function DateTimeInput({
  label,
  value,
  minDate,
  maxDate,
  disabled,
  onValueChange,
  popperPlacement = 'bottom-start',
  renderInPortal = true,
  className,
  containerProps,
  autoFocus,
  preSlot,
  preventOpenOnFocus,
}: DateTimeInputProps) {
  const selectedDate = useMemo(() => parseDateTimeValue(value), [value]);

  const handleChange = useCallback(
    (date: Date | null) => {
      // `date` holds the user's wall-clock selection; formatting it keeps the local
      // UTC offset, producing an ISO 8601 string (e.g. 2026-06-05T17:00:00+05:00).
      onValueChange?.(date ? dayjs(date).format() : null);
    },
    [onValueChange]
  );

  const customInput = useMemo(
    () => <Input label={label} preSlot={preSlot} containerProps={containerProps} />,
    [label, preSlot, containerProps]
  );

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleChange}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      autoFocus={autoFocus}
      strictParsing={false}
      showTimeSelect
      timeIntervals={TIME_INTERVAL_MINUTES}
      timeCaption="Time"
      showMonthDropdown
      showYearDropdown
      shouldCloseOnSelect={false}
      dateFormat={DATE_TIME_FORMAT}
      popperPlacement={popperPlacement}
      portalId={renderInPortal ? 'root-portal' : undefined}
      calendarClassName={CALENDAR_CLASS}
      className={twMerge(className)}
      customInput={customInput}
      preventOpenOnFocus={preventOpenOnFocus}
    />
  );
}
